/**
 * As contas da conversa — sem uma linha de desenho.
 *
 * Quem entra no mesmo bloco, de quem é o nome de cada mensagem, o que colapsa em
 * galeria e como o arquivo aparece: é o que decide a leitura da mesa, e é a única
 * parte da tela que dá para prender em teste sem navegador. O componente que usa
 * isso (`componentes/Trilha.jsx`) fica só com a pintura.
 */
import { corAvatar, diaDe, hojeDe, hora, ontemDe } from "./visual.jsx";
import { avatarDoRegistro } from "./api.js";

/* ---------------------------------------------------------------- formatos */

export const EXTENSAO = (nome) =>
  String(nome ?? "")
    .split(".")
    .pop()
    ?.toLowerCase() ?? "";

const FAMILIA = {
  jpg: "imagem",
  jpeg: "imagem",
  png: "imagem",
  webp: "imagem",
  gif: "imagem",
  mp3: "audio",
  ogg: "audio",
  /* O que celular manda de verdade: `.oga` do Android, `.aac`, `.amr` do
     aparelho antigo. Fora desta lista o recado de voz caía em cartão cinza
     "arquivo" — com o áudio lá dentro, sem play. */
  oga: "audio",
  opus: "audio",
  aac: "audio",
  amr: "audio",
  m4a: "audio",
  wav: "audio",
  mp4: "video",
  mov: "video",
  m4v: "video",
  "3gp": "video",
  mkv: "video",
  webm: "video",
  pdf: "pdf",
  vcf: "contato",
};

/**
 * Que cara o arquivo tem na tela.
 *
 * Vale o nome do arquivo, não o `tipo` da mensagem: o WhatsApp manda áudio como
 * `document` com frequência, e quem sabe tocar é a extensão.
 */
export const familiaDoAnexo = (nome) => FAMILIA[EXTENSAO(nome)] ?? "arquivo";

/** Endereço do anexo. `pasta` é a do cliente, que vem em `GET /api/mesa/:id`. */
export const urlDoAnexo = (pasta, nome) =>
  pasta && nome ? `/api/arquivo/${encodeURIComponent(pasta)}/${encodeURIComponent(nome)}` : null;

/**
 * O endereço de um arquivo do CHECKLIST.
 *
 * A mensagem guarda só o nome do arquivo (a pasta vem do caso), mas o item do
 * checklist guarda a REFERÊNCIA — "marina-costa-alves/rg-e-cpf.pdf", com a
 * pasta dentro. Passar isso como nome montava `/api/arquivo/marina/marina%2Frg`
 * e a conferência abria "Arquivo não encontrado" com o arquivo no disco: era o
 * que se via no vídeo da auditoria, quatro documentos em branco na tela de
 * aprovar.
 */
export function urlDoDocumento(pasta, referencia) {
  if (!referencia) return null;
  const corte = String(referencia).lastIndexOf("/");
  return corte < 0 ? urlDoAnexo(pasta, referencia) : urlDoAnexo(referencia.slice(0, corte), referencia.slice(corte + 1));
}

/** Só o nome do arquivo, sem a pasta — é o que se mostra para gente. */
export const nomeDoArquivo = (referencia) =>
  String(referencia ?? "")
    .split("/")
    .pop() ?? "";

/**
 * O nome que vai na mensagem.
 *
 * É o autor DAQUELA mensagem, não o titular do caso: um atendimento passa pelo
 * SDR, pelo advogado e pela IA, e a conversa que assina tudo com o mesmo nome
 * esconde exatamente a informação que o sócio procura quando algo saiu errado.
 */
export function quemFalou(mensagem, caso, equipe) {
  if (mensagem.autor === "copiloto")
    return { nome: "IA · copiloto", cor: "var(--color-verde)", tinta: "var(--color-verde-vivo)", ia: true };

  if (mensagem.direcao === "entrada") {
    /* `whatsapp:baileys`, `webhook:meta`: autor técnico de quando o CANAL
       era gravado no lugar de quem falou. Quem manda numa conversa de entrada é
       o cliente — a conversa mostrava o nome do adaptador acima da foto que a
       pessoa mandou, e as mensagens antigas continuam no banco assim. */
    const tecnico = /^(whatsapp|webhook|liderhub|meta|baileys):/i.test(String(mensagem.autor ?? ""));
    const doCliente = !mensagem.autor || mensagem.autor === "cliente" || tecnico;
    return {
      nome: doCliente ? (caso?.registro?.nome ?? "Cliente") : mensagem.autor,
      /* A foto do WhatsApp do cliente, a mesma da lista. Sem `tem_avatar` aqui a
         imagem pode não existir — o Avatar cai nas iniciais no erro. */
      foto: doCliente && caso?.registro?.id ? avatarDoRegistro(caso.registro.id) : null,
      tinta: "var(--color-terra)",
      cliente: true,
    };
  }

  const autor = String(mensagem.autor ?? "").toUpperCase();
  /*
   * Autor técnico da SAÍDA — o que o CRM grava quando não foi ninguém da mesa.
   *
   * `aparelho` é o que o escritório digitou no celular; `escritório` é o que veio
   * na importação do histórico; `liderhub` é mensagem ANTIGA, de quando a
   * revenda existia (saiu em 30/08, e as linhas dela ficam no banco). O nome do
   * bloco escrevia isso cru, em minúscula, ao lado de "CAMILA" — a entrada já
   * traduzia o autor técnico, a saída não.
   */
  const DE_FORA = { APARELHO: "Escritório · celular", ESCRITÓRIO: "Escritório", LIDERHUB: "Escritório · LiderHub" };
  const deFora = DE_FORA[autor];
  if (deFora) return { nome: deFora, foto: null, cor: corAvatar(deFora), tinta: "var(--color-verde-fundo)" };
  /* Pelo nome do cadastro OU pelo nome do login (`apelidos`): o autor gravado é
     quem estava logado, e os dois cadastros nasceram separados. */
  const pessoa = (equipe ?? []).find(
    (p) => p.nome.toUpperCase() === autor || (p.apelidos ?? []).some((a) => a.toUpperCase() === autor),
  );
  /* Sem autor gravado a mensagem saiu por integração (régua, webhook): "Escritório"
     é honesto, inventar o nome de quem está logado não é. */
  const nome = pessoa?.nome ?? mensagem.autor ?? "Escritório";
  return { nome, foto: pessoa?.avatar ?? null, cor: pessoa?.cor ?? corAvatar(nome), tinta: "var(--color-verde-fundo)" };
}

/** Duas mensagens seguidas da mesma pessoa em cinco minutos são um bloco. */
const JUNTAS_ATE = 5 * 60 * 1000;
const instante = (q) => Date.parse(String(q ?? "").replace(" ", "T") + "Z") || 0;
/* O dia no fuso de quem lê, e não os dez primeiros caracteres do texto do banco
   (que é UTC): à noite, "hoje" no banco já é amanhã, e a conversa abria um
   divisor de dia falso no meio das mensagens das 21h. */
const dia = (q) => diaDe(q);

const DIA_LONGO = { weekday: "long", day: "numeric", month: "long" };

/** "hoje", "ontem" ou o dia escrito — como no aplicativo de mensagem. */
export function rotuloDoDia(iso) {
  if (!iso) return "";
  /* Hoje e ontem no relógio de quem lê: com `toISOString` (UTC), depois das 21h
     de Goiânia a conversa de hoje deixava de se chamar "HOJE". */
  if (iso === hojeDe()) return "HOJE";
  if (iso === ontemDe()) return "ONTEM";
  return new Date(iso + "T12:00:00Z").toLocaleDateString("pt-BR", DIA_LONGO).toUpperCase();
}

export const rotuloCurto = (iso) => new Date(iso + "T12:00:00Z").toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });

/**
 * Agrupa a conversa em blocos, dia por dia.
 *
 * A tela desenhava avatar, nome e hora em toda mensagem — e uma pessoa que manda
 * seis linhas seguidas virava seis cabeçalhos iguais. Aqui o bloco carrega o
 * cabeçalho uma vez e as bolhas se encostam; a hora fica na última, que é a que
 * responde "quando isso terminou".
 */
export function agrupar(mensagens, caso, equipe) {
  const blocos = [];
  let ultimoDia = null;

  for (const m of mensagens) {
    const quem = quemFalou(m, caso, equipe);
    const nota = m.tipo === "note";
    const diaDela = dia(m.quando);
    const abreDia = diaDela !== ultimoDia;
    ultimoDia = diaDela;

    const anterior = blocos[blocos.length - 1];
    const emenda =
      anterior &&
      !abreDia &&
      !nota &&
      !anterior.nota &&
      anterior.quem.nome === quem.nome &&
      anterior.direcao === m.direcao &&
      instante(m.quando) - instante(anterior.mensagens[anterior.mensagens.length - 1].quando) <= JUNTAS_ATE;

    if (emenda) anterior.mensagens.push(m);
    else blocos.push({ chave: m.id, dia: diaDela, abreDia, nota, quem, direcao: m.direcao, mensagens: [m] });
  }
  return blocos;
}

/**
 * Os blocos, embrulhados por DIA.
 *
 * Existe por causa do chip de data que fica preso no topo da conversa: `sticky`
 * gruda o elemento dentro do PAI dele, então o divisor solto no primeiro bloco
 * do dia descolava assim que aquele bloco acabava — o chip sumia no meio da
 * quinta-feira e voltava na sexta. Com um embrulho por dia, o chip acompanha o
 * dia inteiro e é empurrado para fora pelo dia seguinte, que é exatamente o
 * "troca ao chegar no próximo" do aplicativo.
 */
export function porDia(blocos) {
  const dias = [];
  for (const bloco of blocos ?? []) {
    const ultimo = dias[dias.length - 1];
    if (ultimo && ultimo.dia === bloco.dia) ultimo.blocos.push(bloco);
    else dias.push({ dia: bloco.dia, chave: `dia-${bloco.dia}-${bloco.chave}`, blocos: [bloco] });
  }
  return dias;
}

/**
 * Mídia seguida é uma peça só.
 *
 * O cliente manda as cinco fotos do RG em cinco mensagens sem texto nenhum — na
 * conversa isso virava cinco bolhas com uma foto cada, e a tela inteira era uma
 * pessoa mandando documento. Aqui a sequência colapsa numa galeria: uma peça,
 * uma hora, um rodapé.
 */
export function emFaixas(mensagens, anexoDe) {
  const faixas = [];
  for (const m of mensagens) {
    const anexo = anexoDe(m);
    /*
     * Figurinha nunca entra em galeria.
     *
     * Três figurinhas seguidas são três FALAS ("😂", "👍", "🎉"), não as cinco
     * fotos do RG: colapsá-las numa grade com moldura e "3 arquivos" embaixo
     * transforma piada em anexo de processo. Cada uma fica na sua bolha, solta,
     * como no aplicativo.
     */
    const figurinha = m.tipo === "sticker";
    const soMidia = Boolean(anexo) && !figurinha && !(m.texto ?? m.transcricao);
    const ultima = faixas[faixas.length - 1];

    if (figurinha) {
      faixas.push({ chave: m.id, mensagem: m, anexo });
      continue;
    }

    if (soMidia && ultima?.midia) {
      ultima.midia.push(anexo);
      ultima.mensagens.push(m);
      continue;
    }
    if (soMidia) faixas.push({ chave: m.id, midia: [anexo], mensagens: [m] });
    else faixas.push({ chave: m.id, mensagem: m, anexo });
  }
  /* Mídia sozinha continua bolha: galeria de um item é só uma bolha com moldura
     a mais. */
  return faixas.map((f) => (f.midia?.length === 1 ? { chave: f.chave, mensagem: f.mensagens[0], anexo: f.midia[0] } : f));
}

/* ------------------------------------------------------------------- texto */

/* Endereço, telefone entre parênteses e o resto. A pontuação final fica FORA do
   link: "veja em https://x.com/a." tem um ponto que é da frase, não da URL. */
const ENDERECO = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
const SOBRA = /[.,;:!?)\]}'"»]+$/;

/**
 * Quebra o texto da mensagem em pedaços de texto e de link.
 *
 * O escritório manda link a toda hora — o contrato para assinar, o vídeo de como
 * baixar a CTPS, o processo no site do tribunal. Como texto cru, a pessoa tinha
 * de selecionar e copiar à mão, e quem lê a conversa depois não sabia que aquilo
 * era clicável. Aqui vira pedaço com `href`, e quem pinta decide como mostra.
 */
export function emPedacos(texto) {
  const bruto = String(texto ?? "");
  if (!bruto) return [];
  const pedacos = [];
  let fim = 0;

  for (const achado of bruto.matchAll(ENDERECO)) {
    const inicio = achado.index;
    let endereco = achado[0];
    const sobra = endereco.match(SOBRA)?.[0] ?? "";
    if (sobra) endereco = endereco.slice(0, -sobra.length);

    if (inicio > fim) pedacos.push({ tipo: "texto", valor: bruto.slice(fim, inicio) });
    pedacos.push({ tipo: "link", valor: endereco, href: endereco.startsWith("www.") ? `https://${endereco}` : endereco });
    if (sobra) pedacos.push({ tipo: "texto", valor: sobra });
    fim = inicio + achado[0].length;
  }
  if (fim < bruto.length) pedacos.push({ tipo: "texto", valor: bruto.slice(fim) });
  return pedacos;
}

/** O domínio, para o rótulo curto do link: `rocha101.duckdns.org/assinar/…`. */
export function encurtarEndereco(endereco, limite = 42) {
  const limpo = String(endereco).replace(/^https?:\/\//i, "");
  return limpo.length <= limite ? limpo : `${limpo.slice(0, limite - 1)}…`;
}

/* -------------------------------------------------------------------- vCard */

/* Desdobra o texto do vCard: linha continuada começa com espaço ou tabulação, e
   é assim que um endereço longo chega. */
const desdobrar = (texto) =>
  String(texto ?? "")
    .replace(/\r\n|\r/g, "\n")
    .replace(/\n[ \t]/g, "");

/** `TEL;TYPE=CELL;WAID=5547...:+55 47 8915-9567` → { chave, tipo, valor }. */
function linhaDoVCard(linha) {
  const corte = linha.indexOf(":");
  if (corte < 0) return null;
  const [nome, ...parametros] = linha.slice(0, corte).split(";");
  const valor = linha.slice(corte + 1).trim();
  if (!nome || !valor) return null;
  const tipo = parametros
    .map((p) => (p.toUpperCase().startsWith("TYPE=") ? p.slice(5) : p))
    .filter((p) => p && !/^(WAID|PREF|CHARSET|ENCODING)=/i.test(p) && !/^\d+$/.test(p))
    .join(" ");
  return { chave: nome.split(".").pop().toUpperCase(), tipo, valor };
}

/**
 * Lê um cartão de contato (.vcf) para o que a tela mostra.
 *
 * O escritório manda o contato do suporte por vCard, e o cliente manda o do
 * marido, o do colega que também foi demitido. Como "arquivo desconhecido" isso
 * era um retângulo cinza chamado `Escritório Felipe Galera (Suporte).vcf` — o
 * telefone, que é o conteúdo, ficava escondido dentro do arquivo.
 *
 * Devolve `null` quando o texto não é um vCard, e é o chamador que decide o que
 * fazer com isso (aqui: volta a ser cartão de arquivo).
 */
export function lerVCard(texto) {
  const limpo = desdobrar(texto);
  if (!/BEGIN:VCARD/i.test(limpo)) return null;

  const cartao = { nome: "", organizacao: "", cargo: "", telefones: [], emails: [], enderecos: [], site: "" };
  for (const bruta of limpo.split("\n")) {
    const linha = linhaDoVCard(bruta.trim());
    if (!linha) continue;
    const { chave, tipo, valor } = linha;

    if (chave === "FN") cartao.nome = valor;
    // `N` é sobrenome;nome;… — só serve se não veio FN.
    else if (chave === "N" && !cartao.nome) cartao.nome = valor.split(";").filter(Boolean).reverse().join(" ").trim();
    else if (chave === "ORG") cartao.organizacao = valor.replace(/;+$/, "").replace(/;/g, " · ");
    else if (chave === "TITLE") cartao.cargo = valor;
    else if (chave === "TEL") cartao.telefones.push({ tipo: tipo || "telefone", valor });
    else if (chave === "EMAIL") cartao.emails.push({ tipo: tipo || "e-mail", valor });
    else if (chave === "ADR") cartao.enderecos.push(valor.split(";").filter(Boolean).join(", "));
    else if (chave === "URL") cartao.site = valor;
  }
  if (!cartao.nome && !cartao.telefones.length) return null;
  return cartao;
}

/* ------------------------------------------------------------- WhatsApp */

/** `5548999998888` → `+55 48 99999-8888`. Número curto sai como está. */
export function telefoneLegivel(bruto) {
  const d = String(bruto ?? "").replace(/\D/g, "");
  /*
   * Fora da faixa de telefone, nada.
   *
   * Curto demais é LID cortado; comprido demais é id de grupo ou de canal — o
   * `120363404701403742` de uma lista de figurinhas vinha formatado como
   * "+1203634 04 70140-3742", que parece telefone e não é. E.164 termina em 15
   * dígitos.
   */
  if (d.length < 10 || d.length > 15) return "";

  /* O país sai pela frente, e não "os onze últimos são DDD + número": telefone
     fixo brasileiro tem oito dígitos, então `554896324532` é 55 + 48 + 9632-4532
     e cortar pelos onze últimos partia o DDD no meio. */
  let pais = "";
  let resto = d;
  if (d.length >= 12 && d.startsWith("55")) {
    pais = "55";
    resto = d.slice(2);
  } else if (d.length > 11) {
    pais = d.slice(0, d.length - 11);
    resto = d.slice(-11);
  }
  const ddd = resto.slice(0, 2);
  const numero = resto.slice(2);
  const meio = numero.length >= 9 ? `${numero.slice(0, 5)}-${numero.slice(5)}` : `${numero.slice(0, 4)}-${numero.slice(4)}`;
  return `${pais ? `+${pais} ` : ""}${ddd} ${meio}`;
}

/**
 * O nome de uma conversa da caixa do WhatsApp.
 *
 * A ordem é a do aplicativo: nome salvo, nome que o contato publica, telefone. O
 * que ele NÃO faz é mostrar o identificador interno — o WhatsApp novo chama a
 * conversa por LID (`171941147218157@lid`), um número de dezoito dígitos sem
 * relação com o telefone, e a caixa aparecia cheia deles. Sem nome e sem
 * telefone, o honesto é dizer que não há contato salvo.
 */
export function nomeDaConversa(conversa) {
  const nome = String(conversa?.nome ?? "").trim();
  if (nome && !/^\d{12,}(@|$)/.test(nome)) return nome;
  const telefone = telefoneLegivel(conversa?.telefone);
  if (telefone) return telefone;
  if (conversa?.grupo) return "Grupo sem nome";
  return "Sem contato salvo";
}

/* ------------------------------------------------------- histórico e citação */

/**
 * Junta o histórico já carregado, a conversa do servidor e o que está saindo.
 *
 * Três origens, uma lista só: as páginas antigas que a rolagem foi buscando, as
 * mensagens que vieram em `GET /api/mesa/:id` e o eco otimista das que ainda
 * estão subindo. Casa por id porque as três se sobrepõem — o eco tem de sair de
 * cena no instante em que a mensagem de verdade chega do servidor.
 */
export function juntarConversa(antigas, mensagens, pendentes) {
  const vistas = new Set((mensagens ?? []).map((m) => m.id));
  const historico = (antigas ?? []).filter((m) => !vistas.has(m.id));

  /*
   * O eco sai de cena no MESMO quadro em que a mensagem de verdade entra.
   *
   * Antes ele saía num `mudar` posterior ao da releitura: por alguns quadros a
   * mesma frase aparecia DUAS vezes — a bolha do eco e a do servidor — e depois
   * uma delas sumia. Era isso que se via como pisca-pisca ao mandar mensagem, e
   * o pulo da rolagem vinha junto (a conversa crescia e encolhia).
   *
   * O casamento é por texto e direção dentro de uma janela curta: o eco não tem
   * id do servidor para comparar, e o texto que ACABOU de sair é único o
   * bastante — duas mensagens iguais em dois minutos é a mesma mensagem
   * reenviada, não duas falas.
   */
  const JANELA_DO_ECO = 2 * 60 * 1000;
  const instanteDe = (q) => Date.parse(String(q ?? "").replace(" ", "T") + "Z") || 0;
  const jaChegou = (eco) =>
    (mensagens ?? []).some(
      (m) =>
        m.direcao === "saida" &&
        (m.texto ?? "") === (eco.texto ?? "") &&
        Math.abs(instanteDe(m.quando) - instanteDe(eco.quando)) < JANELA_DO_ECO,
    );

  const esperando = (pendentes ?? []).filter((p) => !p.substituida && !jaChegou(p));
  return [...historico, ...(mensagens ?? []), ...esperando];
}

/** Uma mensagem por id EXTERNO — é por ele que o WhatsApp cita. */
export function indiceDeCitadas(mensagens) {
  const mapa = new Map();
  for (const m of mensagens ?? []) if (m.externo_id) mapa.set(String(m.externo_id), m);
  return mapa;
}

/**
 * O que se lê na prévia de uma mensagem citada.
 *
 * Uma linha, sem quebra: a citação é a referência, não a mensagem. Anexo sem
 * texto mostra a família ("foto", "áudio") em vez do nome do arquivo, que num
 * `.oga` do celular do cliente é ruído puro.
 */
export function resumoDaCitada(m, limite = 90) {
  if (!m) return "";
  const texto = String(m.texto ?? m.transcricao ?? "")
    .replace(/\s+/g, " ")
    .trim();
  if (texto) return texto.length > limite ? `${texto.slice(0, limite - 1)}…` : texto;
  const arquivo = m.arquivo || m.anexo;
  /* Sem arquivo, o TIPO ainda diz o que era: a mídia do histórico que não
     veio para o CRM era citada como "mensagem". */
  if (!arquivo) return { audio: "áudio", image: "foto", video: "vídeo", document: "documento" }[m.tipo] ?? "mensagem";
  return { imagem: "foto", audio: "áudio", video: "vídeo", pdf: "PDF", contato: "contato" }[familiaDoAnexo(arquivo)] ?? "arquivo";
}

/**
 * Onde a leitura parou: a primeira mensagem do cliente ainda não lida.
 *
 * Fixado no instante em que a conversa abre — a tela marca tudo como lido em
 * seguida, e um divisor que dependesse do banco viveria zero segundo. É a
 * pergunta de quem volta do almoço com nove mensagens novas: onde eu paro de
 * ler o que já sabia.
 */
export function primeiraNaoLida(mensagens) {
  const naoLidas = (mensagens ?? []).filter((m) => m.direcao === "entrada" && !m.lida_em);
  /* Uma só não vale divisor: a linha "não lidas" acima da última mensagem de uma
     conversa que a pessoa está olhando agora é mobília. */
  return naoLidas.length > 1 ? { id: naoLidas[0].id, quantas: naoLidas.length } : null;
}

/* ------------------------------------------------------------------- busca */

/** Sem acento e sem caixa, como quem procura escreve. */
const semAcento = (t) =>
  String(t ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export const casaComTermo = (m, termo) =>
  Boolean(String(termo ?? "").trim()) &&
  semAcento(`${m.texto ?? ""} ${m.transcricao ?? ""} ${m.arquivo ?? m.anexo ?? ""}`).includes(semAcento(termo));

/**
 * Quais mensagens casam com o termo, da mais nova para a mais antiga.
 *
 * A ordem é a da busca do aplicativo: quem procura "atestado" numa conversa de
 * oito meses quer a última vez que aquilo foi dito, e só depois as anteriores.
 */
export const acharNaConversa = (mensagens, termo) =>
  String(termo ?? "").trim()
    ? (mensagens ?? [])
        .filter((m) => casaComTermo(m, termo))
        .map((m) => m.id)
        .reverse()
    : [];

/** Quebra o texto nos pedaços que casam com o termo, para o realce da busca. */
export function partirPorTermo(texto, termo) {
  const bruto = String(texto ?? "");
  const alvo = String(termo ?? "").trim();
  if (!alvo) return [{ marcado: false, valor: bruto }];
  /* Compara sem acento, mas CORTA no texto original: o que aparece na bolha é o
     que o cliente escreveu, com cedilha e tudo. */
  const plano = semAcento(bruto);
  const agulha = semAcento(alvo);
  const pedacos = [];
  let de = 0;
  for (;;) {
    const achou = plano.indexOf(agulha, de);
    if (achou < 0 || !agulha) break;
    if (achou > de) pedacos.push({ marcado: false, valor: bruto.slice(de, achou) });
    pedacos.push({ marcado: true, valor: bruto.slice(achou, achou + agulha.length) });
    de = achou + agulha.length;
  }
  if (de < bruto.length) pedacos.push({ marcado: false, valor: bruto.slice(de) });
  return pedacos.length ? pedacos : [{ marcado: false, valor: bruto }];
}

/* ------------------------------------------------------------- presença */

/** "digitando…", "gravando…", "online" ou "visto por último ontem às 14:32". */
export function presencaEmPalavras(presenca) {
  if (!presenca?.estado) return null;
  const { estado, visto_em: visto } = presenca;
  if (estado === "composing" || estado === "digitando") return { texto: "digitando…", vivo: true };
  if (estado === "recording" || estado === "gravando") return { texto: "gravando um áudio…", vivo: true };
  if (estado === "disponivel" || estado === "available") return { texto: "online", vivo: true };
  if (!visto) return null;
  const quando = diaDe(visto);
  const dia = quando === hojeDe() ? "hoje" : quando === ontemDe() ? "ontem" : rotuloCurto(quando);
  return { texto: `visto por último ${dia} às ${hora(visto)}`, vivo: false };
}
